import { useMemo, useState, RefObject } from 'react';
import { DateTime } from 'luxon';
import {
  useNylasCalendarEventsQuery,
  useNylasPrimaryCalendarQuery,
  useDeleteNylasCalendarEventMutation,
  useUpdateNylasCalendarMutation,
  NylasCalendarEventDataFragment,
} from '../../../../../generated/graphql';
import { customToast } from '../../../../components/ToastAlert/customToast';
import { useResources } from './useResources';
import { logger } from '../../../../../lib/logger';
import { useCalendars } from './useCalendars';
import { transformEventForCalendar } from '../utils/eventTransformers';
import { isMicrosoftProvider } from '../../../../lib/calendarProvider';
import { useCalendarViewStore } from '../stores/useCalendarViewStore';

export const useCalendarEvents = (
  isNylasConnected: boolean,
  selectedCalendarIds?: string[],
  visibleRange?: {
    start: Date;
    end: Date;
  },
  calendarRef?: RefObject<any>,
  viewType?: 'calendars' | 'resources',
  calendarProvider?: string | null,
) => {
  const titleFilter = useCalendarViewStore((s) => s.titleFilter);
  const [currentDate, setCurrentDate] = useState(DateTime.now());
  const { getResourceByEmail, resources } = useResources();
  const { getCalendarByEmail } = useCalendars();

  const {
    data: nylasPrimaryCalendarData,
    loading: isLoadingNylasPrimaryCalendar,
    refetch: refetchNylasPrimaryCalendar,
  } = useNylasPrimaryCalendarQuery({
    skip: !isNylasConnected,
  });

  const primaryCalendarId = nylasPrimaryCalendarData?.nylasPrimaryCalendar?.id;

  const {
    data: nylasCalendarEventsData,
    loading: isLoadingNylasCalendarEvents,
    refetch: refetchNylasCalendarEvents,
    networkStatus: calendarEventsNetworkStatus,
  } = useNylasCalendarEventsQuery({
    variables: {
      input: {
        calendarIds: selectedCalendarIds || [],
        startTime: visibleRange
          ? DateTime.fromJSDate(visibleRange.start)
          : currentDate.startOf('week'),
        endTime: visibleRange
          ? DateTime.fromJSDate(visibleRange.end)
          : currentDate.endOf('week'),
        includeOnlyResources: viewType === 'resources',
        titleFilter: titleFilter || undefined,
      },
    },
    skip:
      !isNylasConnected ||
      !primaryCalendarId ||
      (viewType !== 'resources' && !selectedCalendarIds?.length),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const [updateNylasCalendar] = useUpdateNylasCalendarMutation();
  const [deleteNylasCalendarEvent, { loading: isDeletingEvent }] =
    useDeleteNylasCalendarEventMutation();

  const handleCalendarColorChange = async (
    calendarId: string,
    hexColor: string,
  ) => {
    try {
      const response = await updateNylasCalendar({
        variables: {
          input: { calendarId, hexColor },
        },
      });

      if (response.data?.updateNylasCalendar) {
        await refetchNylasPrimaryCalendar();
        customToast.success('Calendar color updated');
      }
    } catch (error) {
      customToast.error('Error updating calendar color');
    }
  };

  const handleDeleteEvent = async (event: NylasCalendarEventDataFragment) => {
    try {
      const response = await deleteNylasCalendarEvent({
        variables: {
          input: {
            eventId: event.id,
            calendarId: event.calendarId,
          },
        },
      });

      if (response.data?.deleteNylasCalendarEvent) {
        // Remove the event from the calendar immediately
        const calendarApi = calendarRef?.current?.getApi();
        if (calendarApi) {
          const calendarEvent = calendarApi.getEventById(event.id);
          if (calendarEvent) {
            calendarEvent.remove();
          }
        }

        customToast.success('Event deleted');
        return true;
      }
    } catch (error) {
      customToast.error('Error deleting event');
    }
    return false;
  };

  const transformEventsForCalendar = () => {
    logger.debug('📅 Before transformation', {
      rawEvents: nylasCalendarEventsData?.nylasCalendarEvents,
      rawEventsCount: nylasCalendarEventsData?.nylasCalendarEvents?.length,
      viewType,
      includeOnlyResources: viewType === 'resources',
      selectedCalendarIds,
      calendarIdsLength: selectedCalendarIds?.length,
    });

    const transformedEvents =
      nylasCalendarEventsData?.nylasCalendarEvents
        ?.map((event) => {
          // Match resources based on provider type and view type
          let resource;
          if (viewType === 'resources') {
            // Microsoft: match by resources array (event.resources)
            if (isMicrosoftProvider(calendarProvider)) {
              if (event.resources && event.resources.length > 0) {
                const eventResource = event.resources[0];
                resource = eventResource.email
                  ? getResourceByEmail(eventResource.email)
                  : undefined;
              }
            } else {
              // Google: match by calendarId (resource calendars have calendarId matching resource email)
              resource = getResourceByEmail(event.calendarId);
            }
          } else {
            // For Calendars tab, match by calendarId as before
            resource = getResourceByEmail(event.calendarId);
          }

          const calendar = getCalendarByEmail(event.calendarId);

          return transformEventForCalendar(event, {
            resourceId: resource?.id,
            calendarHexColor: resource?.hexColor || calendar?.hexColor,
          });
        })
        .filter(Boolean) || [];

    logger.debug('📅 After transformation', {
      transformedEvents,
      viewType,
    });

    return transformedEvents;
  };

  const events = useMemo(
    () => transformEventsForCalendar(),
    [
      nylasCalendarEventsData?.nylasCalendarEvents,
      nylasPrimaryCalendarData?.nylasPrimaryCalendar?.id,
      nylasPrimaryCalendarData?.nylasPrimaryCalendar?.hexColor,
      viewType,
      calendarProvider,
      resources,
      getResourceByEmail,
      getCalendarByEmail,
    ],
  );

  return {
    currentDate,
    setCurrentDate,
    events,
    isLoading:
      isLoadingNylasPrimaryCalendar ||
      isLoadingNylasCalendarEvents ||
      calendarEventsNetworkStatus === 4, // Refetching
    isDeletingEvent,
    primaryCalendar: nylasPrimaryCalendarData?.nylasPrimaryCalendar,
    handleCalendarColorChange,
    handleDeleteEvent,
    refetchEvents: refetchNylasCalendarEvents,
  };
};
